import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_STATUT_LABELS,
  AGENCE_BIEN_STATUT_OPTIONS,
  AGENCE_BIEN_STATUT_TONES,
  AGENCE_BIEN_TYPE_LABELS,
  type AgenceBien,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import type { ExportColumn } from "@/utils/export";
import { formatFcfa } from "@/utils/format";
import BienForm from "./components/BienForm";

type SortKey = "recent" | "loyer" | "reference";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "loyer", label: "Loyer décroissant" },
  { value: "reference", label: "Référence (A → Z)" },
];

const statutOptions = [{ value: "", label: "Tous les statuts" }, ...AGENCE_BIEN_STATUT_OPTIONS];

export default function AgenceBiensPage() {
  const { biens, immeubles, removeBien, contratsByBien } = useAgenceData();
  const { proprietaires } = useProprietaires();
  const { can } = useEmployes();
  const [searchParams] = useSearchParams();

  const proprietaireParam = searchParams.get("proprietaire") ?? "";
  const immeubleParam = searchParams.get("immeuble") ?? "";
  const openFromQuery = searchParams.get("nouveau") === "1";

  const [formOpen, setFormOpen] = useState(openFromQuery);
  const [search, setSearch] = useState("");
  const [proprietaireFilter, setProprietaireFilter] = useState(proprietaireParam);
  const [immeubleFilter, setImmeubleFilter] = useState(immeubleParam);
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<AgenceBien | null>(null);
  const [toDelete, setToDelete] = useState<AgenceBien | null>(null);

  const hasImmeubles = immeubles.length > 0;
  const hasBiens = biens.length > 0;

  const stats = useMemo(
    () => ({
      total: biens.length,
      occupes: biens.filter((item) => item.statut === "occupe").length,
      disponibles: biens.filter((item) => item.statut === "disponible").length,
    }),
    [biens]
  );

  const proprietaireOptions = useMemo(
    () => [
      { value: "", label: "Tous les propriétaires" },
      ...proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    ],
    [proprietaires]
  );

  const immeubleFilterOptions = useMemo(
    () => [
      { value: "", label: "Tous les immeubles" },
      ...immeubles
        .filter((immeuble) => !proprietaireFilter || immeuble.proprietaireId === proprietaireFilter)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    ],
    [immeubles, proprietaireFilter]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = biens.filter((bien) => {
      const immeuble = immeubles.find((item) => item.id === bien.immeubleId);
      const owner = proprietaires.find((item) => item.id === bien.proprietaireId);
      const matchesProprietaire = !proprietaireFilter || bien.proprietaireId === proprietaireFilter;
      const matchesImmeuble = !immeubleFilter || bien.immeubleId === immeubleFilter;
      const matchesStatut = !statutFilter || bien.statut === statutFilter;
      const matchesSearch =
        !term ||
        [
          bien.reference,
          bien.numero,
          bien.etage,
          AGENCE_BIEN_TYPE_LABELS[bien.type],
          immeuble?.nom ?? "",
          owner ? proprietaireNomComplet(owner) : "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesProprietaire && matchesImmeuble && matchesStatut && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "loyer") return b.loyerMensuel - a.loyerMensuel;
      if (sort === "reference") return a.reference.localeCompare(b.reference, "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [biens, immeubles, proprietaires, search, proprietaireFilter, immeubleFilter, statutFilter, sort]);

  const isFiltering =
    Boolean(search.trim()) || Boolean(proprietaireFilter) || Boolean(immeubleFilter) || Boolean(statutFilter);

  const exportColumns: ExportColumn<AgenceBien>[] = [
    { header: "Référence", value: (bien) => bien.reference },
    { header: "Type", value: (bien) => AGENCE_BIEN_TYPE_LABELS[bien.type] },
    {
      header: "Propriétaire",
      value: (bien) => {
        const owner = proprietaires.find((item) => item.id === bien.proprietaireId);
        return owner ? proprietaireNomComplet(owner) : "";
      },
    },
    { header: "Immeuble", value: (bien) => immeubles.find((item) => item.id === bien.immeubleId)?.nom ?? "" },
    { header: "Étage", value: (bien) => bien.etage },
    { header: "Numéro", value: (bien) => bien.numero },
    { header: "Surface (m²)", value: (bien) => bien.surface },
    { header: "Loyer mensuel (FCFA)", value: (bien) => formatFcfa(bien.loyerMensuel) },
    { header: "Statut", value: (bien) => AGENCE_BIEN_STATUT_LABELS[bien.statut] },
  ];

  const columns: DataTableColumn<AgenceBien>[] = [
    {
      key: "reference",
      header: "Référence",
      className: "min-w-[130px]",
      render: (bien) => (
        <span className="font-label font-semibold text-foreground-950">
          {bien.reference || AGENCE_BIEN_TYPE_LABELS[bien.type]}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      className: "min-w-[130px]",
      render: (bien) => <span className="text-foreground-700">{AGENCE_BIEN_TYPE_LABELS[bien.type]}</span>,
    },
    {
      key: "proprietaire",
      header: "Propriétaire",
      className: "min-w-[180px]",
      render: (bien) => {
        const owner = proprietaires.find((item) => item.id === bien.proprietaireId);
        return <span className="text-foreground-700">{owner ? proprietaireNomComplet(owner) : "Non renseigné"}</span>;
      },
    },
    {
      key: "immeuble",
      header: "Immeuble",
      className: "min-w-[160px]",
      render: (bien) => (
        <span className="text-foreground-700">
          {immeubles.find((item) => item.id === bien.immeubleId)?.nom ?? "Non renseigné"}
        </span>
      ),
    },
    {
      key: "surface",
      header: "Surface",
      className: "min-w-[100px]",
      render: (bien) => <span className="text-foreground-700">{bien.surface > 0 ? `${bien.surface} m²` : "—"}</span>,
    },
    {
      key: "loyer",
      header: "Loyer / mois",
      className: "min-w-[130px]",
      render: (bien) => <span className="font-label font-medium text-foreground-950">{formatFcfa(bien.loyerMensuel)}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: (bien) => <Badge tone={AGENCE_BIEN_STATUT_TONES[bien.statut]}>{AGENCE_BIEN_STATUT_LABELS[bien.statut]}</Badge>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[210px]",
      render: (bien) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/agence/biens/${bien.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions
            onEdit={can("biens.modifier") ? () => setEditing(bien) : undefined}
            onDelete={can("biens.supprimer") ? () => setToDelete(bien) : undefined}
          />
        </div>
      ),
    },
  ];

  const handleAdd = () => setFormOpen(true);

  const resetFilters = () => {
    setSearch("");
    setProprietaireFilter("");
    setImmeubleFilter("");
    setStatutFilter("");
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Biens</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Appartements, studios, boutiques et bureaux rattachés aux immeubles des propriétaires. Le propriétaire est
            toujours cohérent avec l'immeuble du bien.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-biens" title="Biens" />
          {hasImmeubles ? (
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un bien
            </button>
          ) : (
            <Link
              to="/agence/immeubles"
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-building-2-line text-base" aria-hidden="true" />
              Créer un immeuble
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-home-4-line" label="Biens" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-home-smile-2-line" label="Biens occupés" value={String(stats.occupes)} tone="secondary" />
        <StatCard icon="ri-home-8-line" label="Biens disponibles" value={String(stats.disponibles)} tone="accent" />
        <StatCard icon="ri-building-2-line" label="Immeubles" value={String(immeubles.length)} tone="neutral" />
      </div>

      {hasBiens && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-bien-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="agence-bien-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Référence, numéro, type, immeuble..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-bien-proprietaire-filter" className="text-xs font-label font-medium text-foreground-700">
                Propriétaire
              </label>
              <Select
                id="agence-bien-proprietaire-filter"
                value={proprietaireFilter}
                onChange={(event) => {
                  setProprietaireFilter(event.target.value);
                  setImmeubleFilter("");
                }}
                options={proprietaireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-bien-immeuble-filter" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="agence-bien-immeuble-filter"
                value={immeubleFilter}
                onChange={(event) => setImmeubleFilter(event.target.value)}
                options={immeubleFilterOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-bien-statut-filter" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="agence-bien-statut-filter"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={statutOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-bien-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="agence-bien-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} bien{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {biens.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {!hasImmeubles && (
        <EmptyState
          icon="ri-building-2-line"
          title="Créez d'abord un immeuble"
          description="Les biens sont rattachés à un immeuble. Commencez par créer un immeuble pour pouvoir ensuite y ajouter vos biens."
          action={
            <Link
              to="/agence/immeubles"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un immeuble
            </Link>
          }
        />
      )}

      {hasImmeubles && !hasBiens && (
        <EmptyState
          icon="ri-home-4-line"
          title="Aucun bien pour le moment"
          description="Ajoutez votre premier bien en le rattachant à l'un de vos immeubles, avec son type, son loyer et son statut."
          action={
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un bien
            </button>
          }
        />
      )}

      {hasBiens && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun bien ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(bien) => bien.id}
          minWidthClass="min-w-[1180px]"
          caption="Biens"
        />
      )}

      {hasBiens && filtered.length > 0 && (
        <p className="text-center text-xs text-foreground-400">
          Statuts disponibles : {Object.values(AGENCE_BIEN_STATUT_LABELS).join(" · ")}.
        </p>
      )}

      <BienForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        defaultProprietaireId={proprietaireParam || undefined}
        defaultImmeubleId={immeubleParam || undefined}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeBien(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer le bien"
        description={
          toDelete
            ? `Voulez-vous vraiment supprimer « ${toDelete.reference || AGENCE_BIEN_TYPE_LABELS[toDelete.type]} » ? Cette action est définitive.`
            : ""
        }
        blockedMessage={
          toDelete && contratsByBien(toDelete.id).length > 0
            ? `Ce bien est relié à ${contratsByBien(toDelete.id).length} contrat(s). Retirez ou clôturez d'abord ses contrats.`
            : undefined
        }
      />
    </div>
  );
}